import { useEffect, useRef, useState } from 'react';
import { fill, t } from '@bms2/shared';
import { apiFetch } from '../pages/records/api';
import { reportCaughtInBrowser } from './systemErrors';
import type { MenuCounts } from './NavigationBar';

/** 🔒 Decision 3072 (T2183): the frame's read of the menu's three balloons. */
export const MENU_COUNTS_PATH = '/shell/menu-counts';

/** A page that changed a count fires this, and the frame reads the counts again. */
export const MENU_COUNTS_EVENT = 'bms2:menu-counts';

/** What the route answers; a count the viewer may not see is null. */
export interface MenuCountsAnswer {
  dispatchUnbooked: number | null;
  myJobsNew: number | null;
  approvalsMine: number | null;
}

/** Ask the frame to read the counts again, after a page wrote something one of them counts. */
export function refreshMenuCounts(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(MENU_COUNTS_EVENT));
}

/** The three balloons' words and the one-count reading of each (T2251), so a screen reader never hears "1 jobs". */
const COUNT_WORDS = {
  dispatch: { many: 'shell.menuCount.dispatch', one: 'shell.menuCount.dispatchOne' },
  myJobs: { many: 'shell.menuCount.myJobs', one: 'shell.menuCount.myJobsOne' },
  approvals: { many: 'shell.menuCount.approvals', one: 'shell.menuCount.approvalsOne' },
  myJobsColumn: { many: 'myJobs.newCount', one: 'myJobs.newCountOne' },
} as const;

/** The words a screen reader hears for a count: the singular form at one, the plural with the figure in it otherwise. */
export function countLabel(which: keyof typeof COUNT_WORDS, count: number): string {
  const words = COUNT_WORDS[which];
  return count === 1 ? t(words.one) : fill(t(words.many), { count: String(count) });
}

/** 🔒 The answer keyed by the menu entry each count sits on, with the words a screen reader hears for it. */
export function menuCountsOf(answer: MenuCountsAnswer | null): MenuCounts {
  if (!answer) return {};
  const rows: [string, number | null, keyof typeof COUNT_WORDS][] = [
    ['nav:dispatch', answer.dispatchUnbooked, 'dispatch'],
    ['nav:my-jobs', answer.myJobsNew, 'myJobs'],
    ['nav:approvals', answer.approvalsMine, 'approvals'],
  ];
  return Object.fromEntries(rows.filter((row): row is [string, number, keyof typeof COUNT_WORDS] => row[1] !== null && row[1] > 0).map(([key, count, which]) => [key, { count, label: countLabel(which, count) }]));
}

/**
 * 🔒 The balloons' counts, read once the frame knows its menu, again on every
 * page the person opens, and again when a page says it changed one. 🔨 A frame
 * whose menu carries none of the three entries asks nothing.
 */
export function useMenuCounts(apiBaseUrl: string, wanted: boolean, pathname: string, answer?: MenuCountsAnswer | null): MenuCounts {
  const [fetched, setFetched] = useState<MenuCountsAnswer | null>(null);
  const [asked, setAsked] = useState(0);
  /** The count of asks the last read answered, so a read set off by a page saying a count moved is told to skip the server's short hold (T2251). */
  const answeredAsk = useRef(0);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const again = () => setAsked((n) => n + 1);
    window.addEventListener(MENU_COUNTS_EVENT, again);
    return () => window.removeEventListener(MENU_COUNTS_EVENT, again);
  }, []);
  useEffect(() => {
    if (answer !== undefined || !wanted) return undefined;
    const controller = new AbortController();
    // 🔨 A page opening may be answered from the figure the frame's own read just walked; a page saying a count moved never is.
    const fresh = asked !== answeredAsk.current;
    answeredAsk.current = asked;
    apiFetch(`${apiBaseUrl}${MENU_COUNTS_PATH}${fresh ? '?fresh=1' : ''}`, { signal: controller.signal })
      .then((response) => {
        // 🔨 A refused or failed read keeps the last good counts (T2251): a transient 5xx must not take every balloon away.
        if (!response.ok) throw new Error(`The menu counts read answered ${response.status}.`);
        return response.json() as Promise<MenuCountsAnswer>;
      })
      .then((body) => {
        if (!controller.signal.aborted) setFetched(body);
      })
      .catch((failed: unknown) => {
        if (!controller.signal.aborted) reportCaughtInBrowser('shell.menuCounts.read', failed);
      });
    return () => controller.abort();
  }, [answer, apiBaseUrl, wanted, pathname, asked]);
  return menuCountsOf(answer !== undefined ? answer : fetched);
}
